import { useLayoutEffect, useRef, useState } from 'react';

import { useLocalStorage } from './use-local-storage';

interface Position {
    x: number;
    y: number;
}

const margin = 12;
const topMargin = 24;

export const useDraggable = (open: boolean, storageKey: string) => {
    const ref = useRef<HTMLDivElement>(null);
    const drag = useRef<Position>(null);
    const lastDragPosition = useRef<Position>(null);
    const [savedPosition, setSavedPosition] = useLocalStorage<Position>(storageKey, null);
    const [position, setPosition] = useState<Position>(null);

    const constrain = (next: Position): Position => {
        const rect = ref.current.getBoundingClientRect();
        return {
            x: Math.max(margin, Math.min(next.x, window.innerWidth - rect.width - margin)),
            y: Math.max(margin, Math.min(next.y, window.innerHeight - rect.height - margin))
        };
    };

    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            drag.current = null;
            return undefined;
        }
        const handleResize = () => {
            const preferred = savedPosition ?? {
                x: (window.innerWidth - ref.current.getBoundingClientRect().width) / 2,
                y: topMargin
            };
            setPosition(constrain(preferred));
        };
        handleResize();
        const observer = new ResizeObserver(handleResize);
        observer.observe(ref.current);
        window.addEventListener('resize', handleResize);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [open, savedPosition]);

    const handlePointerDown = (event: React.PointerEvent<HTMLElement>) => {
        if (event.button !== 0) return;
        const rect = ref.current.getBoundingClientRect();
        drag.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        lastDragPosition.current = null;
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
    };
    const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
        if (!drag.current) return;
        lastDragPosition.current = constrain({ x: event.clientX - drag.current.x, y: event.clientY - drag.current.y });
        setPosition(lastDragPosition.current);
    };
    const handleLostPointerCapture = () => {
        if (drag.current && lastDragPosition.current) setSavedPosition(lastDragPosition.current);
        drag.current = null;
    };

    return {
        handleLostPointerCapture,
        handlePointerDown,
        handlePointerMove,
        position,
        ref
    };
};
